import React, { useState, useEffect } from "react";
import {
  //   Grid, // REMOVIDO: Eliminando o componente Grid que causa o erro TS2769
  CircularProgress,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Collapse,
} from "@mui/material";
import Anuncio from "../Anuncio"; // Importando o componente Anuncio
import { API_URLS } from "../../config/apiUrls";
import { SelectChangeEvent } from "@mui/material/Select";
import { API_IBGE } from "../../config/apiIBGE";
import Pagination from "@mui/material/Pagination"; // Importando o componente de paginação
import FilterListIcon from "@mui/icons-material/FilterList";
import { AD_SEGMENTS, AdSegment } from "../../../domain/adSegments";
import { ordenarAnunciosPorCriacao } from "../../../domain/ordenarAnunciosPorCriacao";
import { filtrarAnunciosParaAnalise } from "../../../domain/filtrarAnunciosParaAnalise";

// Definindo o tipo para os anúncios (inalterado)
interface AnuncioData {
  id: string;
  title: string;
  description: string;
  reasonStatus: string;
  status: string;
  value: string | null;
  offer: string;
  address: string;
  site: string;
  email: string;
  wpp: string;
  youtube: string;
  instagram: string;
  facebook: string;
  apple_st: string;
  play_st: string;
  location: string | null;
  isDeleted: boolean;
  hasFreePlanUsage?: boolean;
  approvedAt: string | null;
  expiresAt: string;
  financialRegister: string | null;
  segment: AdSegment | null;
  category: {
    name: string;
  };
  // A API passou a devolver de 1 a 3 subcategorias por anúncio (array),
  // no lugar do antigo objeto único `subcategory`.
  subcategories: Array<{
    id: string;
    name: string;
  }>;
  images: Array<{
    imageUrl: string;
    orderIndex: number;
  }>;
  createdAt: string;
  updatedAt: string;
  isCountryWide: boolean; // Nova chave
  user: {
    name: string; // Nome do usuário
  };
  payments: Array<{
    status: string; // Status do pagamento
  }>;
  ufs: Array<{
    id: number;
    name: string;
    abbreviation: string;
  }>;
  cities: Array<{
    id: string;
    name: string;
  }>;
  comments: Array<{
    comment: string;
  }>;
}

interface AnaliseAnunciosProps {
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
}

const AnaliseAnuncios: React.FC<AnaliseAnunciosProps> = ({ token, role }) => {
  const [anuncios, setAnuncios] = useState<AnuncioData[]>([]);
  const [filteredAnuncios, setFilteredAnuncios] = useState<AnuncioData[]>([]);
  const [currentAnuncios, setCurrentAnuncios] = useState<AnuncioData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedSegment, setSelectedSegment] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("asc");
  const [isCountryWide, setIsCountryWide] = useState<boolean>(false);

  // Filtros para cidade e estado
  const [filterByCity, setFilterByCity] = useState<string>("");
  const [filterByState, setFilterByState] = useState<string>("");
  const [filterByStateSigla, setFilterByStateSigla] = useState<string>("");
  const [cities, setCities] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Novos estados para o filtro de data
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // NOVO ESTADO PARA BUSCA POR TÍTULO
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Paginação
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(15);
  //Salvar página
  const [previousPage, setPreviousPage] = useState<number | null>(null);

  // Novo estado para armazenar o total de anúncios filtrados
  const [totalAnuncios, setTotalAnuncios] = useState<number>(0);

  const [showFilters, setShowFilters] = useState<boolean>(false);

  const handleUF = (id: string, sigla: string) => {
    setFilterByState(id);
    setFilterByStateSigla(sigla);
  };

  const fetchStates = async () => {
    try {
      const response = await fetch(`${API_IBGE.estados}`);
      const data = await response.json();

      const sortedStates = data.sort(
        (a: { nome: string }, b: { nome: string }) => {
          return a.nome.localeCompare(b.nome);
        },
      );
      setStates(sortedStates);
    } catch (err) {
      setError("Erro ao carregar estados");
    }
  };

  const handleAdUpdated = () => {
    setPreviousPage(currentPage);
    fetchAnuncios();
  };

  const handlePageUpdated = () => {
    setCurrentPage(1);
    setTimeout(() => {
      if (previousPage !== null) {
        setCurrentPage(previousPage);
      }
    }, 200);
  };

  // Função para buscar os anúncios via API (inalterada)
  const fetchAnuncios = async () => {
    setIsLoading(true);
    try {
      // Adicionando o parâmetro "limit" na URL da requisição
      const response = await fetch(`${API_URLS.ads}?limit=100000`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setAnuncios(data.ads);
      applyFiltersAndPagination();
      handlePageUpdated();
    } catch (error) {
      console.error("Erro ao buscar anúncios:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndPagination = () => {
    let filtered = filtrarAnunciosParaAnalise(anuncios);

    // NOVO FILTRO: Por Título (searchTerm)
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((anuncio) =>
        anuncio.title.toLowerCase().includes(lowerCaseSearchTerm),
      );
    }

    // Filtrando os anúncios por status
    if (selectedStatus) {
      filtered = filtered.filter(
        (anuncio) => anuncio.status === selectedStatus,
      );
    }

    if (selectedSegment) {
      filtered = filtered.filter(
        (anuncio) => anuncio.segment === selectedSegment,
      );
    }

    // Apply location filters only if the role is not PARTNER
    if (role !== "PARTNER") {
      // Filtrando os anúncios por estado ou cidade
      if (filterByCity) {
        filtered = filtered.filter((anuncio) =>
          anuncio.cities.some((city) => city.id === filterByCity),
        );
      } else if (filterByState) {
        filtered = filtered.filter((anuncio) =>
          anuncio.ufs.some((uf) => uf.id === parseFloat(filterByState)),
        );
      }

      // Se o filtro de anúncios nacionais (isCountryWide) estiver marcado
      if (isCountryWide) {
        filtered = filtered.filter((anuncio) => anuncio.isCountryWide);
      }
    }

    // Filtrando por data de criação (createdAt)
    if (startDate) {
      const start = new Date(startDate);
      // Set time to the beginning of the day for accurate range
      start.setUTCHours(0, 0, 0, 0);
      filtered = filtered.filter((anuncio) => {
        const createdAtDate = new Date(anuncio.createdAt);
        return createdAtDate >= start;
      });
    }

    if (endDate) {
      const end = new Date(endDate);
      // Set time to the end of the day for accurate range
      end.setUTCHours(23, 59, 59, 999);
      filtered = filtered.filter((anuncio) => {
        const createdAtDate = new Date(anuncio.createdAt);
        return createdAtDate <= end;
      });
    }

    // Contabilizando o número total de anúncios
    setTotalAnuncios(filtered.length);

    const possuiFiltrosAtivos = Boolean(
      searchTerm ||
        selectedStatus ||
        selectedSegment ||
        filterByCity ||
        filterByState ||
        isCountryWide ||
        startDate ||
        endDate,
    );

    filtered = possuiFiltrosAtivos
      ? sortAnuncios(filtered)
      : ordenarAnunciosPorCriacao(filtered);

    // Atualizando os estados
    setFilteredAnuncios(filtered);

    // Paginando os anúncios para a página atual
    const currentAnuncios = filtered.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );

    // Atualizando a lista de anúncios para a página atual
    setCurrentAnuncios(currentAnuncios);
  };

  // Função para ordenar anúncios (inalterada)
  const sortAnuncios = (anuncios: AnuncioData[]) => {
    // Define a ordem de status
    const statusOrder: { [key: string]: number } = {
      PENDING: 1,
      APPROVED: 2,
      REJECTED: 3,
      BLOCKED: 4,
    };

    const now = new Date();

    return anuncios.sort((a, b) => {
      // Primeiro, compara o status
      const statusComparison = statusOrder[a.status] - statusOrder[b.status];
      if (statusComparison !== 0) {
        return statusComparison;
      }

      // Se o status for o mesmo e forem APPROVED, aplica a lógica de ordenação específica
      if (a.status === "APPROVED" && b.status === "APPROVED") {
        const expiresAtA = new Date(a.expiresAt);
        const expiresAtB = new Date(b.expiresAt);
        const approvedAtA = a.approvedAt ? new Date(a.approvedAt) : null;
        const approvedAtB = b.approvedAt ? new Date(b.approvedAt) : null;

        const aExpired = expiresAtA < now;
        const bExpired = expiresAtB < now;

        // Anúncios expirados vão para o final
        if (aExpired && !bExpired) {
          return 1;
        }
        if (!aExpired && bExpired) {
          return -1;
        }

        // Se ambos estão expirados ou ambos não estão expirados, ordena por approvedAt (do mais recente ao mais antigo)
        if (approvedAtA && approvedAtB) {
          return approvedAtB.getTime() - approvedAtA.getTime();
        }
        // Se approvedAt for nulo para algum, ordena por expiresAt (para manter alguma consistência, ou você pode ajustar)
        return expiresAtA.getTime() - expiresAtB.getTime();
      }

      // Se o status for o mesmo (e não for APPROVED), ordena por data de atualização (ou criação)
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return dateA.getTime() - dateB.getTime(); // Ordenar por data (crescente)
    });
  };

  // Função para limpar todos os filtros (inalterada)
  const clearFilters = () => {
    setSelectedStatus("");
    setSelectedSegment("");
    setSortOrder("asc");
    setIsCountryWide(false);
    setFilterByCity("");
    setFilterByState("");
    setFilterByStateSigla("");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
  };

  useEffect(() => {
    fetchStates();
    fetchAnuncios();
  }, [token]);

  // Função para buscar cidades quando um estado for selecionado (inalterada)
  useEffect(() => {
    if (filterByState) {
      const fetchCities = async () => {
        try {
          const response = await fetch(
            `${API_IBGE.cidades}/${filterByStateSigla}`,
          );
          const data = await response.json();
          setCities(data);
        } catch (err) {
          setError("Erro ao carregar cidades");
        }
      };

      fetchCities();
    } else {
      setCities([]);
    }
  }, [filterByState]);

  useEffect(() => {
    applyFiltersAndPagination();
  }, [
    anuncios,
    selectedStatus,
    selectedSegment,
    filterByState,
    filterByCity,
    isCountryWide,
    startDate,
    endDate,
    searchTerm,
    currentPage,
  ]);

  // Função para mudar a página (inalterada)
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography gutterBottom variant="h2">
        Análise de Anúncios
      </Typography>

      {/* BOTÃO QUE CONTROLA A VISIBILIDADE DOS FILTROS */}
      <Box mb={2}>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
        </Button>
      </Box>

      {/* COLLAPSE QUE ENVOLVE OS FILTROS ADICIONAIS */}
      <Collapse in={showFilters}>
        {/* Filtros existentes */}
        <Box sx={{ mb: 2 }}>
          <Typography gutterBottom variant="h5">
            Filtros:
          </Typography>

          {/* NOVO CAMPO DE BUSCA POR TÍTULO */}
          <Box mb={3}>
            <TextField
              label="Buscar por Título do Anúncio"
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />
          </Box>

          {/* Filtro de intervalo de datas */}
          {role !== "ANALYST" && (
            // Substituindo <Grid container spacing={2}> e <Grid item> por Box Flexbox
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              {/* Data de Início */}
              <Box>
                <TextField
                  label="Data de Início"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              {/* Data de Fim */}
              <Box>
                <TextField
                  label="Data de Fim"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="status-select-label">Status</InputLabel>
          <Select
            labelId="status-select-label"
            value={selectedStatus}
            label="Status"
            onChange={(e) => setSelectedStatus(e.target.value)}
            fullWidth
          >
            <MenuItem value="">
              <em>Todos</em>
            </MenuItem>
            <MenuItem value="PENDING">Pendente</MenuItem>
            <MenuItem value="APPROVED">Aprovado</MenuItem>
            <MenuItem value="REJECTED">Rejeitado</MenuItem>
            <MenuItem value="BLOCKED">Bloqueado</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="segment-select-label">Categoria</InputLabel>
          <Select
            labelId="segment-select-label"
            value={selectedSegment}
            label="Categoria"
            onChange={(e) => {
              setSelectedSegment(e.target.value);
              setCurrentPage(1);
            }}
          >
            <MenuItem value="">
              <em>Todos</em>
            </MenuItem>
            {AD_SEGMENTS.map((segment) => (
              <MenuItem key={segment.value} value={segment.value}>
                {segment.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filtro para Pagamentos Nacionais - Condicionalmente renderizado */}
        {role !== "PARTNER" && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCountryWide}
                  onChange={() => setIsCountryWide(!isCountryWide)}
                />
              }
              label="Filtrar por anúncios veiculados para todo Brasil"
            />
          </Box>
        )}

        {/* Filtros de Estado e Cidade - Condicionalmente renderizados (mantido como Flexbox) */}
        {role !== "PARTNER" && (
          <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={filterByState}
                onChange={(e) => {
                  const selectedState = states.find(
                    (state) => state.id === e.target.value,
                  );
                  if (selectedState) {
                    handleUF(selectedState.id, selectedState.sigla);
                  }
                }}
                label="Estado"
                disabled={isCountryWide}
              >
                <MenuItem value="">
                  <em>Todos os Estados</em>
                </MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.nome} ({state.sigla})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Cidade</InputLabel>
              <Select
                value={filterByCity}
                onChange={(e) => setFilterByCity(e.target.value)}
                label="Cidade"
                disabled={isCountryWide}
              >
                <MenuItem value="">
                  <em>Todas as Cidades</em>
                </MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city.codigo_ibge} value={city.codigo_ibge}>
                    {city.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Botão para aplicar os filtros - Opcional, pois a busca reage automaticamente no useEffect */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={applyFiltersAndPagination}
          >
            Aplicar Filtros
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearFilters}
            sx={{ ml: 2 }}
          >
            Limpar Filtros
          </Button>
        </Box>
      </Collapse>
      {/* Informações no topo */}
      <Box mb={3}>
        <Typography variant="h5" component="div">
          Número de anúncios: {totalAnuncios}
        </Typography>
      </Box>

      {/* Loader enquanto os anúncios estão sendo carregados */}
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            height: "80vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        // Substituindo <Grid container spacing={3}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            margin: "-12px", // Simula spacing={3} (24px, compensado com -12px)
          }}
        >
          {/* Exibe os anúncios filtrados */}
          {currentAnuncios.length === 0 ? (
            <Box sx={{ textAlign: "center", width: "100%" }}>
              <h3>Não há anúncios para exibir</h3>
            </Box>
          ) : (
            currentAnuncios.map((anuncio) => (
              // Substituindo <Grid item xs={12} sm={6} md={4} key={anuncio.id}>
              <Box
                key={anuncio.id}
                sx={{
                  boxSizing: "border-box",
                  padding: "12px",
                  width: {
                    xs: "100%", // 1 card
                    sm: "50%", // 2 cards
                    md: "30%", // 3 cards
                    lg: "30%", // 3 cards (Se você aplicou o ajuste anterior)
                  },
                }}
              >
                <Anuncio
                  token={token}
                  role={role}
                  anuncio={anuncio}
                  onAdUpdated={handleAdUpdated}
                />
              </Box>
            ))
          )}
        </Box>
      )}

      {/* Paginação */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={Math.ceil(totalAnuncios / itemsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default AnaliseAnuncios;
